import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  WebAuthnAbortService,
  browserSupportsWebAuthn,
  startAuthentication,
} from '@simplewebauthn/browser';
import { useI18n } from '@bravemove/i18n/react';
import { Banner, Button, OTPInput } from '@bravemove/ui-web';
import { CompassWordmark } from '@bravemove/design-tokens/brand';
import { ShieldIcon } from '@bravemove/design-tokens/icons';
import { adminSecurityKeysApi } from '../api/securityKeys';
import { useAuth } from '../auth/useAuth';
import { translateApiError } from '../auth/errors';
import { translateSecurityKeyError } from '../auth/securityKeyErrors';
import { ApiError } from '../auth/types';

interface LocationState {
  readonly challengeToken?: string;
  /** F65 — this admin has a security key, so offer it above the code. */
  readonly securityKeyAvailable?: boolean;
  /**
   * Deep-link target captured by ProtectedRoute (`state.from`) and threaded
   * through LoginPage so a re-authenticated admin lands back where they were.
   */
  readonly from?: string;
}

/**
 * F06 — MFA challenge. F65: admins with a security key get it as the primary
 * action, with a pulse ring while the browser waits for the tap, and the TOTP
 * code below it. No recovery-code flow exists server-side, so the footer carries
 * truthful lost-device guidance instead (a super admin resets MFA from the Admin
 * users page).
 */
export function LoginTotpPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const state = (location.state ?? null) as LocationState | null;
  const challengeToken = state?.challengeToken ?? null;
  const redirectTo = state?.from ?? '/';
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Kept apart from `error` so a failed key doesn't paint the code boxes red.
  const [keyError, setKeyError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [waitingForKey, setWaitingForKey] = useState(false);
  const offerKey = state?.securityKeyAvailable === true && browserSupportsWebAuthn();

  useEffect(() => {
    if (!challengeToken) {
      navigate('/login', { replace: true });
    }
  }, [challengeToken, navigate]);

  // Leaving the page must not leave the browser prompt open behind it.
  useEffect(() => () => WebAuthnAbortService.cancelCeremony(), []);

  if (!challengeToken) return null;

  /** The challenge token lasts 5 minutes; past that only a new sign-in helps. */
  const describeError = (err: unknown): string =>
    err instanceof ApiError && err.code === 'TOKEN_INVALID'
      ? t('admin:totp.challengeExpired')
      : translateSecurityKeyError(err, t);

  const signInWithKey = async () => {
    if (waitingForKey || submitting) return;
    setWaitingForKey(true);
    setError(null);
    setKeyError(null);
    try {
      const { challengeId, options } = await adminSecurityKeysApi.beginLogin(challengeToken);
      const assertion = await startAuthentication({ optionsJSON: options });
      await auth.confirmSecurityKey(challengeToken, challengeId, assertion);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setKeyError(describeError(err));
    } finally {
      setWaitingForKey(false);
    }
  };

  const submit = async (override?: string) => {
    const value = override ?? code;
    if (value.length !== 6) return;
    setSubmitting(true);
    setError(null);
    setKeyError(null);
    try {
      await auth.confirmTotp(challengeToken, value);
      // S-15 — honor the deep-link the admin originally requested (captured by
      // ProtectedRoute as `state.from`) instead of always landing on the
      // dashboard.
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === 'TOKEN_INVALID'
          ? t('admin:totp.challengeExpired')
          : translateApiError(err, t),
      );
      setCode('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg flex flex-col">
      <header className="px-10 py-6 border-b border-hairline flex items-center justify-between">
        <CompassWordmark size={18} />
        <div className="text-[12px] font-mono uppercase tracking-wider text-ink-3">
          {t('admin:totp.stepBadge')}
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-[520px] rounded-2xl bg-surface border border-hairline p-10 shadow-[0_16px_40px_-20px_rgba(0,0,0,0.20)]">
          <div className="size-16 rounded-2xl bg-accent-soft text-accent inline-flex items-center justify-center mb-5">
            <ShieldIcon size={32} color="currentColor" />
          </div>
          <h1 className="text-h2 font-bold text-ink tracking-tight">{t('admin:totp.title')}</h1>
          <p className="text-body text-ink-2 mt-2">
            {offerKey ? t('admin:totp.subtitleWithKey') : t('admin:totp.subtitle')}
          </p>

          {offerKey ? (
            <>
              <button
                type="button"
                aria-busy={waitingForKey}
                disabled={waitingForKey || submitting}
                onClick={() => void signInWithKey()}
                className="mt-6 w-full flex items-center gap-4 rounded-xl border border-accent/40 bg-bg p-4 text-start transition-colors enabled:hover:bg-surface-2 disabled:cursor-default focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-text"
              >
                <span className="relative inline-flex">
                  {waitingForKey ? (
                    <span
                      aria-hidden="true"
                      className="absolute inset-0 rounded-full bg-accent/30 animate-ping"
                    />
                  ) : null}
                  <span className="relative size-11 rounded-full bg-accent text-accent-ink inline-flex items-center justify-center">
                    <ShieldIcon size={22} color="currentColor" />
                  </span>
                </span>
                <span className="flex-1">
                  <span className="block text-[14px] font-semibold text-ink">
                    {t('admin:totp.hardwareKeyTitle')}
                  </span>
                  <span className="block text-[10.5px] font-mono uppercase tracking-wider text-ink-3 mt-0.5">
                    {waitingForKey
                      ? t('admin:totp.hardwareKeyWaiting')
                      : t('admin:totp.hardwareKeySubtitle')}
                  </span>
                </span>
              </button>

              <div className="flex items-center gap-3 mt-6">
                <div className="flex-1 h-px bg-hairline-2" />
                <div className="text-[10px] font-mono uppercase tracking-wider text-ink-3">
                  {t('admin:totp.orEnterCode')}
                </div>
                <div className="flex-1 h-px bg-hairline-2" />
              </div>
            </>
          ) : null}

          {error || keyError ? (
            <Banner tone="danger" className="mt-4">
              {error ?? keyError}
            </Banner>
          ) : null}

          <form
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
            className="mt-5 flex flex-col items-center gap-4"
          >
            <OTPInput
              value={code}
              onChange={setCode}
              onComplete={(value) => void submit(value)}
              autoFocus
              invalid={!!error}
              disabled={submitting || waitingForKey}
            />
            <Button
              type="submit"
              variant="primary"
              fullWidth
              loading={submitting}
              disabled={code.length !== 6 || submitting || waitingForKey}
            >
              {t('admin:totp.submit')}
            </Button>
          </form>

          {/* S-07 — there is no recovery-code flow server-side (admin auth ships
              login/totp/forgot/reset/invite/enroll only), so the old dead
              "Use recovery code" button is replaced with truthful lost-device
              guidance: only a super admin can reset MFA (Admin users page). */}
          <div className="mt-5 flex items-start justify-between gap-4">
            <p className="text-[12px] leading-snug text-ink-3 max-w-[300px]">
              {offerKey
                ? t('admin:totp.lostDeviceHelpWithKey')
                : t('admin:totp.lostDeviceHelp', {
                    defaultValue:
                      'Lost your authenticator? Ask a super admin to reset your MFA from the Admin users page — you will re-enroll at your next sign-in.',
                  })}
            </p>
            <Button
              type="button"
              variant="link"
              size="sm"
              onClick={() => navigate('/login', { replace: true })}
            >
              {t('admin:forgotPassword.backToLogin')}
            </Button>
          </div>
        </div>
      </main>

      <footer className="px-10 py-3 border-t border-hairline flex items-center justify-between text-[10px] font-mono uppercase tracking-wider text-ink-3">
        <span>{t('admin:totp.footerSession')}</span>
      </footer>
    </div>
  );
}
